import React, { useCallback } from 'react';
import type { ReactNode } from 'react';

import { move } from '@dnd-kit/helpers';
import type { DragEndEvent } from '@dnd-kit/react';
import { DragDropProvider } from '@dnd-kit/react';

import { DragHandle, SortableItem, SortableOverlay } from './components';

interface BaseItem {
  id: string | number;
}

interface Props<T extends BaseItem> {
  items: T[];
  onChange(items: T[]): void;
  renderItem(item: T, index: number): ReactNode;
}

export function SortableList<T extends BaseItem>({ items, onChange, renderItem }: Props<T>) {
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.canceled) return;
      onChange(move(items, event));
    },
    [items, onChange]
  );

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <ul role="application" className="flex flex-col">
        {items.map((item, index) => (
          <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
        ))}
      </ul>
      <SortableOverlay>
        {(source) => {
          const activeItem = items.find((item) => item.id === source.id);
          const activeIndex = items.findIndex((item) => item.id === source.id);
          if (!activeItem) return null;
          return renderItem(activeItem, activeIndex);
        }}
      </SortableOverlay>
    </DragDropProvider>
  );
}

SortableList.Item = SortableItem;
SortableList.DragHandle = DragHandle;
